import clientActions from 'mage-client/src/actions'
import * as configSelectors from 'mage-config/src/selectors'
import * as conversationActions from 'mage-conversation/src/actions'
import * as conversationConstants from 'mage-conversation/src/constants'
import * as conversationSelectors from 'mage-conversation/src/selectors'
import coreActions from 'mage-core/src/actions'
import * as coreSelectors from 'mage-core/src/selectors'
import * as Sentry from 'mage-deps/@sentry/browser'
import i18nActions from 'mage-i18n/src/actions'
import * as i18nSelectors from 'mage-i18n/src/selectors'
import Insights from 'mage-insights'
import realMoment from 'moment'
import * as R from 'ramda'
import { internalChatOverrides } from './config'

import * as selectors from './selectors'

/** THUNKS **/
const saveLanguage = (userId, language, { moment = realMoment, localStorage = window.localStorage } = {}) => (dispatch, getState) => {
  const client = coreSelectors.clientFactory(getState())('/api/users')
  const locale = i18nSelectors.locale(getState())

  dispatch(i18nActions.language.set(language))
  moment.locale(locale)
  try {
    localStorage.setItem('language', language)
  } catch (err) { }

  const env = configSelectors.environment(getState())
  if (env === 'development') {
    document.cookie = `KEYCLOAK_LOCALE=${language}; path=/auth/realms/merchants/;`
  } else {
    let domain = '.klarna.com'
    if (env === 'staging') {
      domain = '.klarna.net'
    }
    document.cookie = `KEYCLOAK_LOCALE=${language}; path=/auth/realms/merchants/; domain=${domain}`
  }

  if (coreSelectors.isMerchantsRealm(getState())) {
    return client.request({
      method: 'PUT',
      url: `/users/${userId}`,
      data: {
        language
      }
    })
      .catch(Sentry.captureException)
  } else {
    return Promise.resolve()
  }
}

const initWidget = (client, mid, settings, user, features) => async (dispatch, getState) => {
  await dispatch(conversationActions.getWidgetData(client))

  if (window.fcWidget) {
    if (conversationSelectors.markets(getState()).length > 0) {
      const locale = () => {
        /*
        locale needed to send MSAT in correct langauge
        if there are multiple channels (e.g. for sweden and germany)
        we don't know which one the user is talking in
        meaning we won't know the language the MSAT needs to be sent in
        THEREFORE:
        We only send the MSAT if there is currently only one market available for this user
        TODO:
        Find a way to send the MSAT for in the correct language for each channel
      */
        const countries = conversationSelectors.markets(getState()) || []
        return countries.length === 1 ? conversationConstants.LOCALES[countries[0]] : null
      }

      settings.tags = selectors.channels(getState())
        .map(tag => {
          const override = internalChatOverrides[tag]

          if (override && features[override.feature]) {
            return override.tag
          }

          return tag
        })

      settings.externalId = user.email
      settings.restoreId = conversationSelectors.restoreId(getState()) || null

      const getAppAccess = (access) => {
        return Object.keys(access)
          .filter(app => app !== 'account') // "account" is not an app
          .map(app => {
            const appName = app.split('-')[0]
            const appAccess = access[app].map(r => r.replace('merchant-', '')).join('/')
            return `${appName} (${appAccess})`
          }).toString()
      }

      const userSettings = {
        firstName: user.givenName,
        lastName: user.familyName,
        email: user.email,
        process: 'MS', // to differentiate 'CS', 'MS', and 'feedback' Smart Plugs in Freshchat
        isMerchantPortal: true,
        mids: user.merchantIds.toString(),
        possibleMids: user.merchantIds.toString(), // only for backwards compatibility for smart plug, use 'mids' now
        selectedMid: mid,
        appAccess: user.resourceAccess.toString(),
        access: getAppAccess(user.resourceAccess), // only for backwards compatibility for smart plug, use 'appAccess' now
        market: locale() // for automated resolve-message
      }

      window.fcWidget.init(settings)
      window.fcWidget.user.setProperties(userSettings)

      // Save restoreId if this is a new user
      window.fcWidget.on('user:created', (resp) => {
        if (resp && resp.data && resp.data.restoreId && !settings.restoreId) {
          dispatch(conversationActions.saveRestoreId(client, resp.data.restoreId))
        }
      })

      window.fcWidget.on('widget:opened', () => {
        Insights.event({
          category: 'support_chat',
          action: 'widget_opened',
          label: 'support_chat:widget_opened'
        })
      })
    } else {
      window.fcWidget.destroy()
    }
  }
}

const initialiseKeycloak = (options = {}) => async (dispatch, getState) => {
  const {
    keycloak = selectors.getKeycloak(getState())
  } = options
  await keycloak.init({
    onLoad: 'login-required',
    promiseType: 'native',
    checkLoginIframe: false
  })
  dispatch(coreActions.token.set(keycloak))
}

const refreshToken = (options = {}) => async (dispatch, getState) => {
  const {
    minValidity = selectors.keycloakConfig(getState()).refreshTokenThreshold,
    force = false,
    keycloak = selectors.getKeycloak(getState())
  } = options
  try {
    // NOTE: updateToken _requires_ an arg (ugh), so give it a day (assumes tokens expire in less time)
    const tokenWasRefreshed = await keycloak.updateToken(force ? 3600 : minValidity)
    if (tokenWasRefreshed) {
      if (keycloak.isTokenExpired(minValidity)) {
        throw new Error(`Token expiring in less than ${minValidity} seconds`)
      }
      dispatch(coreActions.token.set(keycloak))
    }
  } catch (err) {
    keycloak.clearToken()
  }
}

const logout = () => (dispatch, getState) => {
  const keycloak = selectors.getKeycloak(getState())
  const appBaseUrl = selectors.getAppBaseUrl(getState())
  dispatch(coreActions.keycloak.realm.clear())
  keycloak.logout({ redirectUri: appBaseUrl })
}

const fetch = (url, config = {}) => async (dispatch, getState) => {
  const getClient = () => url.startsWith('/')
    ? coreSelectors.clientFactory(getState())()
    : coreSelectors.createClient(getState())

  try {
    return await getClient().request(url, config)
  } catch (error) {
    if (R.pathEq(['response', 'status'], 401, error)) {
      // if 401 response, refresh the token and try again
      await dispatch(refreshToken({ force: true }))
      return getClient().request(url, config)
    } else {
      throw error
    }
  }
}

const fetchCapabilities = (options = {}) => async dispatch => {
  dispatch(clientActions.capabilities.loading())
  const response = await dispatch(fetch('capabilities', options))
  dispatch(clientActions.capabilities.set(response))
}

export {
  saveLanguage,
  initWidget,
  initialiseKeycloak,
  refreshToken,
  logout,
  fetch,
  fetchCapabilities
}
